'use client';

import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Building2 } from 'lucide-react';
import type { AdscripcionContextoResponse, ItemAdscripcion, UnidadesCatalogoResponse } from '@siiv/schemas';
import { ModalShell } from '@/components/ui/ModalShell';
import { notify } from '@/components/ui/BrandToaster';
import formStyles from '@/components/ui/form.module.css';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { ApiRequestError } from '@/lib/apiClient';
import { aplicarCambiosInmediatos, getFilasDeUnidades } from '@/lib/adscripcion-edicion';
import { emitirPerfilActualizado } from '@/lib/perfil-actualizado';
import { createSolicitud, getAdscripcionContexto, getUnidadesCatalogo } from '@/lib/solicitudes';
import { AdscripcionFilas } from './AdscripcionFilas';
import { AdscripcionPildora } from './AdscripcionPildora';
import {
	ALTO_MODAL_ADSCRIPCION,
	ANCHO_MIN_MODAL_ADSCRIPCION,
	ANCHO_MODAL_ADSCRIPCION,
	nombresDeUnidades,
	type ModoAdscripcion,
	type UnidadCatalogoMin,
} from './adscripcionModel';
import {
	agregarUnidad,
	clasificarCambios,
	construirBase,
	construirPlan,
	descripcionDeSolicitudEnviada,
	edicionInicial,
	establecerPrincipal,
	estadoBase,
	fijarMotivo,
	filasParaMostrar,
	hacerPrincipal,
	hayEstructurales,
	hayInmediatos,
	itemsPorFila,
	moverUnidad,
	objetivoDe,
	otraEsNueva,
	pildoraDeAdscripcion,
	pildoraInicialDeAdscripcion,
	pildoraSolicitudPendiente,
	quitarUnidad,
	razonNoEnviable,
	restaurarUnidad,
	textoBotonGuardar,
	type BaseEdicion,
	type EdicionUnidades,
} from './adscripcionEdicion';
import { construirVistaPrevia } from './adscripcionPreview';

const CATALOGO_VACIO: UnidadCatalogoMin[] = [];
const NINGUNA_LINEA: string[] = [];
const SIN_ITEMS: ReadonlyMap<number, ItemAdscripcion> = new Map();

/** What the admin direct mode sends: the full target state plus the optional "Otra" name. */
export interface PayloadAdscripcionDirecta {
	idUnidadPrincipal: number;
	idUnidadesAdicionales: number[];
	nombreOtra: string | null;
}

interface PropsComunes {
	onClose: () => void;
	/** Width requested by the request type registry or by the caller. */
	maxWidth?: string;
	/** Profile whose adscription is edited. Defaults to the signed-in researcher's own profile. */
	slug?: string;
	/**
	 * Called after the order or the name of "Otra" were applied (they apply at once), so a screen that shows
	 * the units from its own state can reload it. Not called when an administrator's direct change (which reloads
	 * that state itself) carried them.
	 */
	onCambiosAplicados?: () => void | Promise<unknown>;
}

interface PropsSolicitud extends PropsComunes {
	modo?: 'solicitud';
	/** Called after the request was created so the caller can refresh its list. */
	onSolicitudCreada?: () => void;
}

interface PropsDirecto extends PropsComunes {
	modo: 'directo';
	/**
	 * Applies the target state (the owner of the screen does the PUT, then revalidates and refreshes what is
	 * open). A rejection keeps the modal open and shows its message.
	 */
	onAplicarDirecto: (payload: PayloadAdscripcionDirecta) => Promise<unknown>;
}

export type AdscripcionSolicitudModalProps = PropsSolicitud | PropsDirecto;

type Carga =
	| { estado: 'cargando' }
	| { estado: 'error'; mensaje: string }
	| {
			estado: 'listo';
			contexto: AdscripcionContextoResponse;
			catalogo: UnidadesCatalogoResponse['unidades'];
			base: BaseEdicion | null;
	  };

const FORM_ID = 'adscripcion-solicitud-form';
const MENSAJE_FALLO_CARGA = 'No pudimos cargar tu adscripción. Reintenta en unos segundos.';
const MENSAJE_SIN_PERFIL = 'Tu cuenta no tiene un perfil de investigador vinculado, por lo que no hay adscripción que editar.';
const MENSAJE_FALLO_GENERICO = 'No se pudo completar la operación. Reintenta en unos segundos.';

/**
 * Container of the adscription modal: ONE row per unit (principal first), everything staged until submit. The order
 * and the name of "Otra" apply at once on submit (personal-data endpoints); adding, removing and changing
 * the principal become a request (a researcher; an administrator resolves it) or, for an administrator, are applied
 * directly. Request mode and direct mode share this one screen: the two entry points never diverge (UAR-04).
 * It is a form modal: Cancelar/Cerrar discards what was staged and there is no "deshacer cambios". What needs approval and
 * what applies at once is said by the pill of the header (a tooltip), not by a block inside the body.
 */
export function AdscripcionSolicitudModal(props: AdscripcionSolicitudModalProps) {
	const { onClose, maxWidth } = props;
	const modo: ModoAdscripcion = props.modo ?? 'solicitud';
	const esDirecto = modo === 'directo';
	const router = useRouter();
	const { user, status } = useAuthSession();
	const slug = props.slug ?? user?.slug ?? null;

	const [cargaRed, setCarga] = useState<Carga>({ estado: 'cargando' });
	// Once the session is known and it has no researcher profile there is nothing to load: say so instead of
	// leaving "Cargando…" up forever. Derived (not an effect), so it needs no extra state.
	const carga: Carga = !slug && status !== 'loading' ? { estado: 'error', mensaje: MENSAJE_SIN_PERFIL } : cargaRed;
	const [edicion, setEdicion] = useState<EdicionUnidades | null>(null);
	const [enviando, setEnviando] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!slug) return;
		let activo = true;
		void (async () => {
			try {
				// Independent requests: fetched together, not one after the other.
				const [contexto, catalogo, filas] = await Promise.all([
					getAdscripcionContexto(slug),
					getUnidadesCatalogo(),
					getFilasDeUnidades(slug),
				]);
				if (!activo) return;
				const base = construirBase(contexto, filas);
				setCarga({ estado: 'listo', contexto, catalogo: catalogo.unidades, base });
				setEdicion(edicionInicial(base));
			} catch {
				if (activo) setCarga({ estado: 'error', mensaje: MENSAJE_FALLO_CARGA });
			}
		})();
		return () => {
			activo = false;
		};
	}, [slug]);

	const listo = carga.estado === 'listo' && edicion !== null;
	const contexto = carga.estado === 'listo' ? carga.contexto : null;
	const base = carga.estado === 'listo' ? carga.base : null;
	const catalogo = carga.estado === 'listo' ? carga.catalogo : CATALOGO_VACIO;
	const nombreOtra = edicion?.nombreOtra;
	const nombres = useMemo(() => nombresDeUnidades(catalogo, nombreOtra), [catalogo, nombreOtra]);

	const cambios = useMemo(() => (edicion ? clasificarCambios(base, edicion) : null), [base, edicion]);
	const filas = useMemo(() => (edicion ? filasParaMostrar(base, edicion) : []), [base, edicion]);
	const razon = useMemo(
		() => (edicion && listo ? razonNoEnviable({ base, edicion, modo, nombres }) : null),
		[base, edicion, modo, listo, nombres],
	);
	// The rows already show the resulting state; the one thing they cannot show is which research lines would close.
	// It comes from the SAME function the server applies (`calcularEfectoAdscripcion`), fed with the held lines of the
	// context, so what the tooltip of the pill says is what an approval does.
	const lineasCerradas = useMemo(() => {
		if (!edicion || !contexto || !cambios) return NINGUNA_LINEA;
		const objetivo = objetivoDe(edicion);
		if (!objetivo) return NINGUNA_LINEA;
		const vista = construirVistaPrevia({
			actual: estadoBase(base),
			items: base?.principal ? cambios.estructurales : [],
			decisiones: [],
			objetivo,
			lineasActivas: contexto.lineasActivas,
			nombres,
		});
		return vista.lineasCerradas.map((linea) => linea.nombre);
	}, [edicion, contexto, cambios, base, nombres]);
	const pildora = useMemo(
		() => (cambios ? pildoraDeAdscripcion(cambios, modo, nombres, lineasCerradas) : pildoraInicialDeAdscripcion(modo)),
		[cambios, modo, nombres, lineasCerradas],
	);
	// Every structural change has its own reason, written inside the row of that change (see `itemsPorFila`).
	const itemsDeFilas = useMemo(() => (cambios ? itemsPorFila(cambios, modo) : SIN_ITEMS), [cambios, modo]);

	const puedeEnviar = listo && razon === null && !enviando;
	const estructural = cambios ? hayEstructurales(cambios) : false;
	// The researcher's own pending request: a second pill in the header (no number, a tooltip with the detail). An administrator
	// editing the units directly does not get it.
	const pildoraPendiente = useMemo(
		() =>
			!esDirecto && contexto
				? pildoraSolicitudPendiente(contexto.solicitudPendiente?.objetivo, estadoBase(base), estructural, nombres)
				: null,
		[esDirecto, contexto, base, estructural, nombres],
	);

	const editar = (cambiar: (actual: EdicionUnidades) => EdicionUnidades) => setEdicion((actual) => (actual ? cambiar(actual) : actual));

	/** After a partial success (the immediate part applied, the rest did not) the screen starts from what is saved now. */
	const recargarBase = async () => {
		if (!slug || carga.estado !== 'listo') return;
		try {
			const [contextoNuevo, filasNuevas] = await Promise.all([getAdscripcionContexto(slug), getFilasDeUnidades(slug)]);
			setCarga({ estado: 'listo', contexto: contextoNuevo, catalogo: carga.catalogo, base: construirBase(contextoNuevo, filasNuevas) });
		} catch {
			// Best effort: the staged edition stays and a new submit re-applies the same writes (they are idempotent).
		}
	};

	/** What the open screens need after something applied: the server tree, the owner's own views and the shell's state. */
	const refrescarPantalla = async (avisarAlLlamador: boolean) => {
		router.refresh();
		if (slug) emitirPerfilActualizado(slug);
		if (avisarAlLlamador) await props.onCambiosAplicados?.();
	};

	const enviar = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!puedeEnviar || !edicion || !slug) return;
		const plan = construirPlan(base, edicion, modo);

		setEnviando(true);
		setError(null);
		let inmediatosAplicados = false;
		try {
			if (hayInmediatos(plan.cambios)) {
				await aplicarCambiosInmediatos(slug, plan.cambios);
				inmediatosAplicados = true;
			}

			if (plan.solicitud) {
				const creada = await createSolicitud(plan.solicitud);
				notify.success('Solicitud enviada', {
					description: descripcionDeSolicitudEnviada(creada.reemplazadas, inmediatosAplicados),
				});
				if (inmediatosAplicados) await refrescarPantalla(true);
				// A request alone changes no profile data, but the "cambio de unidad pendiente" mark must show at once.
				else emitirPerfilActualizado(slug);
				if (props.modo !== 'directo') props.onSolicitudCreada?.();
			} else if (plan.directo && props.modo === 'directo') {
				// The caller applies the structural change and refreshes the whole screen (and says so in its own toast).
				await props.onAplicarDirecto(plan.directo);
			} else {
				notify.success('Cambios guardados', {
					description: esDirecto
						? 'Los cambios se aplicaron y quedaron registrados en la auditoría.'
						: 'Los cambios se aplicaron a tu perfil.',
				});
				await refrescarPantalla(true);
			}
			onClose();
		} catch (causa) {
			const mensaje = causa instanceof Error && causa.message ? causa.message : MENSAJE_FALLO_GENERICO;
			setError(
				inmediatosAplicados
					? `Los cambios que no requieren aprobación ya se aplicaron, pero el resto no. ${mensaje}`
					: mensaje,
			);
			if (!esDirecto && plan.solicitud) {
				notify.error('No se pudo enviar la solicitud', {
					description: causa instanceof ApiRequestError && causa.status < 500 ? mensaje : 'Reintenta en unos segundos.',
				});
			}
			if (inmediatosAplicados) {
				await recargarBase();
				await refrescarPantalla(true);
			}
		} finally {
			setEnviando(false);
		}
	};

	const indicador = !listo
		? null
		: (razon ?? (!esDirecto && estructural ? 'Listo para enviar.' : 'Listo para guardar.'));

	return (
		<ModalShell
			title="Editar afiliación"
			icon={<Building2 size={16} />}
			onClose={onClose}
			onCancel={onClose}
			formId={FORM_ID}
			headerAction={
				<>
					{pildoraPendiente ? <AdscripcionPildora pildora={pildoraPendiente} /> : null}
					<AdscripcionPildora pildora={pildora} />
				</>
			}
			saveText={cambios ? textoBotonGuardar(cambios, modo, enviando) : 'Guardar cambios'}
			cancelText="Cancelar"
			saveDisabled={!puedeEnviar}
			footerHasChanges={razon === null && listo}
			footerIndicatorContent={
				indicador ? (
					<span
						role="status"
						className={`pr-6 text-xs leading-snug ${razon ? 'text-slate-700' : 'text-slate-600'}`}
					>
						{razon ? conEncabezadoResaltado(indicador) : indicador}
					</span>
				) : undefined
			}
			hideFooterIndicator={!listo}
			maxWidth={maxWidth ?? ANCHO_MODAL_ADSCRIPCION}
			minWidth={ANCHO_MIN_MODAL_ADSCRIPCION}
			minHeight={ALTO_MODAL_ADSCRIPCION}
		>
			{carga.estado === 'cargando' ? (
				<p className="py-8 text-center text-sm text-slate-500">Cargando tu adscripción…</p>
			) : carga.estado === 'error' ? (
				<p className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{carga.mensaje}</p>
			) : edicion && cambios ? (
				<form id={FORM_ID} className={`${formStyles.modalForm} min-w-0`} onSubmit={(event) => void enviar(event)}>
					{error ? (
						<p role="alert" className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
							{error}
						</p>
					) : null}
					{base === null && !esDirecto ? (
						<p className="rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
							No tienes una unidad vigente, por lo que no puedes solicitar un cambio. Completa tu registro primero.
						</p>
					) : null}

					<AdscripcionFilas
						filas={filas}
						edicion={edicion}
						catalogo={catalogo}
						nombres={nombres}
						otraNueva={otraEsNueva(base, edicion)}
						pideMotivo={!esDirecto}
						motivos={edicion.motivos}
						itemsPorFila={itemsDeFilas}
						disabled={enviando}
						onMover={(idUnidad, direccion) => editar((actual) => moverUnidad(base, actual, idUnidad, direccion))}
						onHacerPrincipal={(idUnidad) => editar((actual) => hacerPrincipal(base, actual, idUnidad))}
						onQuitar={(idUnidad) => editar((actual) => quitarUnidad(base, actual, idUnidad))}
						onRestaurar={(idUnidad) => editar((actual) => restaurarUnidad(actual, idUnidad))}
						onAgregar={(idUnidad, motivo) => editar((actual) => agregarUnidad(base, actual, idUnidad, motivo))}
						onElegirPrincipal={(idUnidad) => editar((actual) => establecerPrincipal(actual, idUnidad))}
						onNombreOtra={(nombre) => editar((actual) => ({ ...actual, nombreOtra: nombre }))}
						onMotivo={(idItem, texto) => editar((actual) => fijarMotivo(base, actual, idItem, texto))}
					/>
				</form>
			) : null}
		</ModalShell>
	);
}

/**
 * A blocking footer message: its lead-in ("Falta el motivo de:") in amber, so it reads apart from the changes it lists,
 * and the rest with the unit names highlighted.
 */
function conEncabezadoResaltado(texto: string): ReactNode {
	const corte = texto.indexOf(': ');
	if (corte === -1) return conNombresResaltados(texto);
	return (
		<>
			<span className="font-semibold text-amber-700">{texto.slice(0, corte + 1)}</span> {conNombresResaltados(texto.slice(corte + 2))}
		</>
	);
}

/** The footer message with every «quoted» unit name in bold and without its guillemets: easier to scan than the quotes. */
function conNombresResaltados(texto: string): ReactNode[] {
	return texto.split(/«([^»]*)»/).map((parte, indice) =>
		indice % 2 === 1 ? (
			<strong key={indice} className="font-semibold text-slate-900">
				{parte}
			</strong>
		) : (
			parte
		),
	);
}
